<template>
  <div class="push-settings-container pa-4">
    <div v-if="isLoading" class="text-center">
      <v-progress-circular indeterminate color="primary"></v-progress-circular>
      <p class="mt-4">Lade Gerätestatus...</p>
    </div>

    <div v-else>
      <div v-if="isInsideApp">
        <h2 class="title is-4 mb-3">Dieses Gerät</h2>

        <v-card v-if="currentDevice" outlined class="mb-6">
          <v-list-item>
            <v-list-item-icon>
              <v-icon>{{ isMobile ? "mdi-cellphone" : "mdi-desktop-mac-dashboard" }}</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title class="font-weight-bold">{{ currentDevice.deviceInfo.id }}</v-list-item-title>
              <v-list-item-subtitle>Registriert am: {{ formatDate(currentDevice.createdAt) }}</v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
          <v-divider></v-divider>
          <v-card-text>
            <div class="d-flex justify-space-between align-center">
              <div>
                <strong>Status:</strong>
                <v-chip :color="statusColor" small dark class="ml-2">{{ statusText }}</v-chip>
              </div>
              <v-btn small outlined color="info" @click="checkCurrentDeviceStatus" :loading="isChecking"> Status prüfen </v-btn>
            </div>
          </v-card-text>
        </v-card>

        <v-card v-else outlined class="mb-6 text-center pa-5">
          <v-icon size="50" color="warning" class="mb-3">mdi-bell-off-outline</v-icon>
          <h3 class="subtitle-1 font-weight-bold">Push-Benachrichtigungen sind deaktiviert</h3>
          <p class="body-2 text--secondary">Aktiviere Benachrichtigungen, um über Kurse, News und dein Training auf dem Laufenden zu bleiben.</p>
          <v-btn color="primary" @click="activatePush">
            <v-icon left>mdi-bell-ring</v-icon>
            Jetzt aktivieren
          </v-btn>
        </v-card>
      </div>

      <div v-if="otherDevices.length > 0">
        <h2 class="title is-4 mb-3">{{ isInsideApp ? "Weitere registrierte Geräte" : "Registrierte Geräte" }}</h2>
        <v-card outlined>
          <v-list dense>
            <template v-for="(device, index) in otherDevices">
              <v-list-item :key="device._id">
                <v-list-item-icon>
                  <v-icon>mdi-devices</v-icon>
                </v-list-item-icon>

                <v-list-item-content>
                  <v-list-item-title>{{ device.deviceInfo.id }}</v-list-item-title>
                  <v-list-item-subtitle>
                    Status:
                    <v-chip :color="getChipColor(device.status)" x-small dark class="ml-1">{{ getStatusText(device.status) }}</v-chip>
                  </v-list-item-subtitle>
                </v-list-item-content>

                <v-list-item-action class="d-flex flex-row align-center">
                  <v-progress-circular v-if="device.isChecking" indeterminate size="20" width="2" color="grey" class="mr-2"></v-progress-circular>
                  <v-btn icon @click="removeDevice(device)">
                    <v-icon color="red">mdi-delete-outline</v-icon>
                  </v-btn>
                </v-list-item-action>
              </v-list-item>
              <v-divider v-if="index < otherDevices.length - 1" :key="`divider-${device._id}`"></v-divider>
            </template>
          </v-list>
        </v-card>
      </div>
    </div>

    <init-push :visible="showPushPermissionDialog" @acceptPush="handlePushAccepted" @declinePush="handlePushDeclined"></init-push>
  </div>
</template>

<script>
import api from "@/api"
import InitPush from "@/components/notification/initPush.vue"

// Diese Funktion liest die Geräte-ID, die von der Flutter-App im localStorage gespeichert wird.
function getCurrentDeviceId() {
  return localStorage.getItem("deviceInfo")
}

export default {
  name: "PushSettings",
  components: {
    InitPush,
  },
  data() {
    return {
      // HINZUGEFÜGT: Prüft einmal, ob wir uns in der Flutter App befinden.
      isInsideApp: !!window.flutter_inappwebview,
      isLoading: true,
      isChecking: false,
      currentDevice: null,
      otherDevices: [],
      currentDeviceStatus: "unknown",
      showPushPermissionDialog: false,
    }
  },
  computed: {
    statusText() {
      return this.getStatusText(this.currentDeviceStatus)
    },
    statusColor() {
      return this.getChipColor(this.currentDeviceStatus)
    },
    isMobile() {
      if (typeof window === "undefined") return false
      return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
    },
  },
  async created() {
    this.fetchDeviceList()
    window.addEventListener("initPush", this.onPushInitialized)
  },
  destroyed() {
    window.removeEventListener("initPush", this.onPushInitialized)
  },
  methods: {
    async fetchDeviceList() {
      this.isLoading = true
      try {
        // Die Logik funktioniert nahtlos weiter:
        // In der App wird currentId gefunden und die Listen aufgeteilt.
        // Im Browser ist currentId null, und alle Geräte landen in `otherDevices`.
        const currentId = this.isInsideApp ? getCurrentDeviceId() : null
        const allDevices = await api.fetchUserPushSettings()

        if (currentId) {
          this.currentDevice = allDevices.find((d) => d.deviceInfo.id === currentId) || null
          this.otherDevices = allDevices
            .filter((d) => d.deviceInfo.id !== currentId)
            .map((device) => ({ ...device, status: "unknown", isChecking: false }))
        } else {
          this.currentDevice = null
          this.otherDevices = allDevices.map((device) => ({ ...device, status: "unknown", isChecking: false }))
        }

        if (this.currentDevice) {
          this.checkCurrentDeviceStatus()
        }

        if (this.otherDevices.length > 0) {
          this.verifyOtherDevices()
        }
      } catch (error) {
        console.error("Fehler beim Laden der Geräteliste:", error)
      } finally {
        this.isLoading = false
      }
    },
    async checkCurrentDeviceStatus() {
      if (!this.currentDevice) return
      this.isChecking = true
      try {
        const response = await api.verifyPushToken(this.currentDevice.token)
        this.currentDeviceStatus = response.isValid ? "active" : "inactive"
      } catch (e) {
        this.currentDeviceStatus = "inactive"
      } finally {
        this.isChecking = false
      }
    },
    async verifyOtherDevices() {
      const verificationPromises = this.otherDevices.map(async (device) => {
        this.$set(device, "isChecking", true)
        try {
          const response = await api.verifyPushToken(device.token)
          this.$set(device, "status", response.isValid ? "active" : "inactive")
        } catch (e) {
          this.$set(device, "status", "inactive")
        } finally {
          this.$set(device, "isChecking", false)
        }
      })
      await Promise.all(verificationPromises)
    },
    getStatusText(status) {
      const texts = {
        unknown: "Wird geprüft...",
        active: "Aktiv",
        inactive: "Inaktiv",
      }
      return texts[status] || "Unbekannt"
    },
    getChipColor(status) {
      const colors = {
        unknown: "grey",
        active: "green",
        inactive: "red",
      }
      return colors[status] || "grey"
    },
    async removeDevice(deviceToRemove) {
      if (confirm(`Möchtest du das Gerät "${deviceToRemove.deviceInfo.id}" wirklich entfernen?`)) {
        try {
          await api.deleteDevice(deviceToRemove._id)
          this.$toast.success("Gerät entfernt.")
          this.fetchDeviceList()
        } catch (error) {
          this.$toast.error("Fehler beim Entfernen des Geräts.")
        }
      }
    },
    formatDate(dateString) {
      if (!dateString) return "N/A"
      return new Date(dateString).toLocaleString("de-CH")
    },
    activatePush() {
      this.showPushPermissionDialog = true
    },
    handlePushAccepted() {
      this.showPushPermissionDialog = false
      this.$toast.info("Folge den Anweisungen deines Geräts, um Benachrichtigungen zu erlauben.")
      if (window.flutter_inappwebview) {
        window.flutter_inappwebview.callHandler("initPush")
      }
    },
    handlePushDeclined() {
      this.showPushPermissionDialog = false
    },
    onPushInitialized() {
      this.$toast.success("Push-Benachrichtigungen werden synchronisiert...")
      setTimeout(() => {
        this.fetchDeviceList()
      }, 3000)
    },
  },
}
</script>

<style scoped>
.title.is-4 {
  font-size: 1.25rem;
  color: #363636;
}
</style>
